(ns opengb.color)

(def all-colors
  {::red     ["#7a2323" "#a32f2f" "#c88282" "#edd5d5"]
   ::orange  ["#a35029" "#da6a37" "#e9a687" "#f6dcc8"]
   ::yellow  ["#a08835" "#d5b547" "#e4ce88" "#f7f0da"]
   ::green   ["#375e34" "#497d45" "#92b18f" "#dbe5da"]
   ::teal    ["#3f6b59" "#548f77" "#98bcad" "#dde9e4"]
   ::blue    ["#124564" "#185c85" "#749db6" "#d1dee7"]
   ::purple  ["#614150" "#895494" "#b899bf" "#dfd9dc"]
   ::brown   ["#4e4435" "#685a46" "#867b6b" "#e1deda" "#b5a58c"]
   ::pink    ["#793232" "#de7069" "#eba7a5" "#f8e2e1"]
   ::misc    ["#00145A"]
   ::grey    ["#212b32" "#425563" "#83a6bf" "#b7c9d3"]
   ::neutral ["#192026"
              "#212B32"
              "#32404A"
              "#425563"
              "#627d8f"
              "#83A6BF"
              "#B7C9D3"
              "#C5D4DC"
              "#D4DFE5"
              "#E2E9ED"
              "#F1F4F6"
              "#F5F7F8"
              "#F8FAFB"]})

(def energy-types
  {::heat          ::red
   ::gas-heat      ::orange
   ::gas-baseload  ::yellow
   ::elec-baseload ::teal
   ::cool          ::blue
   ::elec-cool     ::blue
   ::elec-heat     ::pink
   ::district      ::purple
   ::oil-heat      ::brown})

(def open-brand
  {::brand-pink       [::pink 1]
   ::brand-brown      [::purple 0]
   ::brand-blue       [::misc 0]
   ::brand-grey       [::grey 1]
   ::brand-light-grey [::grey 3]})

(def misc
  {::positive   ::green
   ::negative   ::red
   ::done       ::blue
   ::incomplete ::pink
   ::selection  ::yellow
   ::primary    ::teal})

;; Blended using https://meyerweb.com/eric/tools/color-blend/#:::hex
(def blended-colors
  {::blue-teal     ["#29585F" "#36767E" "#86ADB2" "#D7E4E6"]
   ::teal-yellow   ["#707A47" "#95A25F" "#BEC59B" "#EAEDDF"]
   ::yellow-orange ["#A26C2F" "#D8903F" "#E7BA88" "#F7E6D1"]
   ::orange-red    ["#8F3A26" "#BF4D33" "#D99485" "#F2D9CF"]})

;; A percentile gradient shared by the map and the performance chart, so a home
;; reads as the same colour in both. Every vector is ordered best (lowest
;; emissions) → worst (highest):
;; - :map     the map's 5 percentile bands at full strength.
;; - :map-dim those 5 bands lightened, to de-emphasise the unselected map pins
;;            while a building is selected.
;; - :chart   the 10-step version the performance bar paints, best → worst;
;;            10 = 2 × 5, so each pair of steps nests inside one of the map's
;;            5 bands and a home reads the same band in both views.

;; Blue (best) through teal, yellow and orange to red (worst); :map / :map-dim
;; are colours 1 and 2 of each palette family.
(def spectral-ramp
  {:map     ["#185c85" "#548f77" "#d5b547" "#da6a37" "#a32f2f"]
   :map-dim ["#749db6" "#98bcad" "#e4ce88" "#e9a687" "#c88282"]
   :chart   ["#185c85" "#33737f" "#4d8979" "#7f9c67" "#b8ad52"
             "#d6a443" "#d8833c" "#d46336" "#bb4933" "#a32f2f"]})

;; cmocean haline body with a saturated-yellow worst end (deep purple → vivid
;; yellow); its luminance increases monotonically across it, so the ordering
;; survives greyscale and all three colour-vision-deficiency types.
(def haline-ramp
  {:map     ["#2a186c" "#14628e" "#3e9886" "#75c969" "#ecdf12"]
   :map-dim ["#948cb6" "#8ab0c6" "#9eccc2" "#bae4b4" "#f6ef88"]
   :chart   ["#2a186c" "#20397b" "#17598a" "#22748b" "#348c88"
             "#4ba37f" "#63b973" "#83cc5e" "#b8d538" "#ecdf12"]})

(def malli:percentile-ramp
  [:map {:closed true}
   [:map     [:vector {:min 5 :max 5}   string?]]
   [:map-dim [:vector {:min 5 :max 5}   string?]]
   [:chart   [:vector {:min 10 :max 10} string?]]])

;; Getters

(defn family
  "Returns all colors for key."
  [color-key]
  (or (get all-colors color-key)
      (get all-colors (energy-types color-key))
      (get all-colors (misc color-key))
      (get blended-colors color-key)
      (when (= color-key ::brand)
        (->> open-brand
             keys
             (map #(get-in all-colors (get open-brand %)))))))

(defn single
  "Returns numbered color for key. Defaults to primary / color 1."
  ([color-key] (or (single color-key 1) (get-in all-colors (get open-brand color-key))))
  ([color-key index] (get (family color-key) index)))

(def map-stroke-dark  (single :opengb.color/neutral 2))
(def map-stroke-light (single :opengb.color/neutral 10))
